import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingDown, Users, UserX, Clock, Star, FileText, Building2, ArrowRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white rounded-lg border border-slate-200 p-5 flex items-start gap-4">
    <div className={`p-2.5 rounded-lg ${color}`}>
      <Icon size={20} className="text-white" />
    </div>
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

export default function ExitReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await axios.get(`${API}/exit/reports`, { headers: getHeaders() });
        setData(res.data);
      } catch (err) {
        /* silent */
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-500">Failed to load exit reports.</div>;

  const { summary, attrition_trend, department_analysis, exit_reasons, resignation_status, recent_exits } = data;

  const resStatusData = Object.entries(resignation_status).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v }));

  return (
    <div data-testid="exit-reports-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Exit Reports & Analytics</h1>
        <p className="text-slate-600">Attrition trends, department analysis, and exit insights</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Total Employees" value={summary.total_employees} sub={`${summary.active_employees} active`} color="bg-blue-500" />
        <StatCard icon={UserX} label="Exited Employees" value={summary.inactive_employees} sub={`${summary.total_resignations} resignations filed`} color="bg-red-500" />
        <StatCard icon={Clock} label="Avg Tenure" value={`${summary.avg_tenure_months} mo`} sub={summary.avg_tenure_months > 0 ? 'of exited employees' : 'No data yet'} color="bg-amber-500" />
        <StatCard icon={Star} label="Exit Satisfaction" value={`${summary.avg_satisfaction_rating}/5`} sub={`${summary.would_recommend_pct}% would recommend`} color="bg-emerald-500" />
      </div>

      {/* Attrition Trend Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-blue-900 mb-4" data-testid="attrition-trend-title">Attrition Trend (12 Months)</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={attrition_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
              <Tooltip />
              <Line yAxisId="left" type="monotone" dataKey="exits" stroke="#ef4444" strokeWidth={2} name="Exits" dot={{ r: 4 }} />
              <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#3b82f6" strokeWidth={2} name="Attrition Rate %" strokeDasharray="5 5" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Department-wise Exit Analysis */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-blue-900 mb-4" data-testid="dept-analysis-title">Department-wise Attrition</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={department_analysis} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="department" width={100} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="active" stackId="a" fill="#3b82f6" name="Active" radius={[0, 0, 0, 0]} />
              <Bar dataKey="exited" stackId="a" fill="#ef4444" name="Exited" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Exit Reasons + Resignation Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-blue-900 mb-4" data-testid="exit-reasons-title">Exit Reasons</h2>
          {exit_reasons.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <FileText size={32} className="mx-auto mb-2" />
              <p className="text-sm">No exit reasons recorded yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {exit_reasons.slice(0, 8).map((r, i) => (
                <div key={r.reason} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700 truncate max-w-[200px]">{r.reason}</span>
                      <span className="text-sm font-bold text-slate-900">{r.count}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="h-2 rounded-full" style={{ width: `${Math.min((r.count / (exit_reasons[0]?.count || 1)) * 100, 100)}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-blue-900 mb-4" data-testid="resignation-status-title">Resignation Status Breakdown</h2>
          {resStatusData.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <FileText size={32} className="mx-auto mb-2" />
              <p className="text-sm">No resignations recorded yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={resStatusData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {resStatusData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Recent Exits Table */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-blue-900 mb-4" data-testid="recent-exits-title">Recent Exits</h2>
        {recent_exits.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Users size={32} className="mx-auto mb-2" />
            <p className="text-sm">No exits recorded yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-bold text-blue-900">Name</th>
                  <th className="text-left py-2 px-3 font-bold text-blue-900">Department</th>
                  <th className="text-left py-2 px-3 font-bold text-blue-900">Role</th>
                  <th className="text-left py-2 px-3 font-bold text-blue-900">Designation</th>
                  <th className="text-left py-2 px-3 font-bold text-blue-900">Exit Date</th>
                </tr>
              </thead>
              <tbody>
                {recent_exits.map((u) => (
                  <tr key={u.id || u.name} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-semibold">{u.name}</td>
                    <td className="py-2 px-3 text-slate-600">{u.department || '-'}</td>
                    <td className="py-2 px-3">
                      <span className="px-2 py-0.5 text-xs font-bold uppercase rounded bg-slate-100 text-slate-700">{u.role}</span>
                    </td>
                    <td className="py-2 px-3 text-slate-600">{u.designation || '-'}</td>
                    <td className="py-2 px-3 text-slate-500">{u.date_of_exit ? new Date(u.date_of_exit).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
